/* Mengenstaffel mit Geschenk-Leiste (LimeAds) */
(() => {
  if (customElements.get('po-bundle')) return;

  const formatMoney = (cents, format) => {
    const value = Number(cents) || 0;
    const fmt = (precision, thousands, decimal) => {
      const fixed = (value / 100).toFixed(precision);
      const [whole, frac] = fixed.split('.');
      const withThousands = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? withThousands + decimal + frac : withThousands;
    };
    return String(format || '€{{amount_with_comma_separator}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount': return fmt(2, ',', '.');
        case 'amount_no_decimals': return fmt(0, ',', '.');
        case 'amount_with_comma_separator': return fmt(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(2, "'", '.');
        case 'amount_no_decimals_with_space_separator': return fmt(0, ' ', ',');
        case 'amount_with_space_separator': return fmt(2, ' ', ',');
        case 'amount_with_period_and_space_separator': return fmt(2, ' ', '.');
        default: return fmt(2, '.', ',');
      }
    });
  };

  const FALLBACK_SWATCHES = {
    schwarz: '#1f1f1f', grau: '#8b8f92', blau: '#3b5a86', rot: '#a8423f',
    'grün': '#5d7a5a', gruen: '#5d7a5a', rosa: '#e4b9bd', beige: '#d8c7ad', weiss: '#ffffff', 'weiß': '#ffffff'
  };

  class PoBundle extends HTMLElement {
    connectedCallback() {
      const configEl = this.querySelector('[data-po-config]');
      if (!configEl) return;
      this.config = JSON.parse(configEl.textContent);
      this.applyHiddenValues();
      this.labels = this.parseLabels(this.config.setLabels);
      this.tiers = Array.from(this.querySelectorAll('.po-tier'));
      this.gifts = Array.from(this.querySelectorAll('[data-gift]'));
      this.atc = this.querySelector('[data-atc]');
      this.errorEl = this.querySelector('[data-error]');

      // Platzhalter-Geschenke (ohne Produkt) nur in der Vorschau zeigen
      const isLive = window.Shopify && Shopify.theme && Shopify.theme.role === 'main';
      if (isLive) {
        this.gifts.filter((g) => g.hasAttribute('data-placeholder')).forEach((g) => g.remove());
        this.gifts = this.gifts.filter((g) => g.isConnected);
      }
      const giftBox = this.querySelector('[data-gifts]');
      if (!this.gifts.length && giftBox) giftBox.remove();
      else if (giftBox) giftBox.style.setProperty('--po-gift-count', this.gifts.length);

      const start = this.config.variants.find((v) => v.id === this.config.currentVariant) || this.config.variants.find((v) => v.available) || this.config.variants[0];
      this.rows = [0, 1, 2].map(() => start.options.slice());
      this.selectedTier = parseInt(this.dataset.defaultTier, 10) || 1;

      this.tiers.forEach((tier) => {
        if (tier.querySelector('.po-tier__badge')) tier.classList.add('po-tier--badge');
        const radio = tier.querySelector('.po-tier__radio');
        radio.addEventListener('change', () => this.selectTier(parseInt(tier.dataset.tier, 10)));
      });
      this.atc.addEventListener('click', () => this.addToCart());
      this.atcPrice = this.querySelector('[data-atc-price]');
      const atcLabelEl = this.querySelector('.po-bundle__atc-label');
      this.atcLabel = atcLabelEl ? atcLabelEl.textContent.trim() : 'In den Warenkorb';
      this.setupDeadline();
      this.setupSticky();

      this.selectTier(this.selectedTier);
    }

    // Hinweis auf das echte Angebotsende (Datum aus den Einstellungen, kein täglicher Neustart)
    setupDeadline() {
      const el = this.querySelector('[data-deadline]');
      if (!el) return;
      const m = String(el.dataset.end || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!m) return;
      const end = new Date(+m[1], +m[2] - 1, +m[3], 23, 59, 59);
      const text = el.querySelector('[data-deadline-text]');
      const time = el.querySelector('[data-deadline-time]');
      const pad = (x) => String(x).padStart(2, '0');
      const tick = () => {
        const now = new Date();
        const ms = end - now;
        if (ms <= 0) { el.hidden = true; clearInterval(this.deadlineTimer); return; }
        const sameDay = now.getFullYear() === end.getFullYear() && now.getMonth() === end.getMonth() && now.getDate() === end.getDate();
        if (sameDay) {
          const sec = Math.floor(ms / 1000);
          text.textContent = 'Angebot endet in';
          time.innerHTML = `<b>${pad(Math.floor(sec / 3600))}</b><i>Std</i><b>${pad(Math.floor((sec % 3600) / 60))}</b><i>Min</i><b>${pad(sec % 60)}</b><i>Sek</i>`;
          time.hidden = false;
        } else {
          text.innerHTML = `Angebot gilt bis <strong>${pad(end.getDate())}.${pad(end.getMonth() + 1)}.</strong>`;
          time.hidden = true;
        }
        el.hidden = false;
      };
      tick();
      this.deadlineTimer = setInterval(tick, 1000);
    }

    setupSticky() {
      const sticky = this.querySelector('[data-sticky]');
      if (!sticky || !('IntersectionObserver' in window)) return;
      this.sticky = sticky;
      this.stickyLabel = sticky.querySelector('[data-sticky-label]');
      this.stickyPrice = sticky.querySelector('[data-sticky-price]');
      this.stickyBtn = sticky.querySelector('[data-sticky-btn]');
      // Aus der Box lösen, damit kein Eltern-Element das position: fixed stört
      sticky.style.setProperty('--po-accent', getComputedStyle(this).getPropertyValue('--po-accent'));
      document.body.appendChild(sticky);
      this.stickyBtn.addEventListener('click', () => {
        if (this.isAtcAbove()) this.addToCart();
        else this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      // Der Observer meldet nur Wechsel sichtbar/unsichtbar, die Beschriftung hängt aber an der Scrollrichtung
      window.addEventListener('scroll', () => this.renderSticky(), { passive: true });
      new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting;
        sticky.classList.toggle('is-visible', show);
        document.body.classList.toggle('po-sticky-on', show);
        sticky.setAttribute('aria-hidden', String(!show));
        this.stickyBtn.tabIndex = show ? 0 : -1;
        this.renderSticky();
      }).observe(this.atc);
    }

    renderSticky() {
      if (!this.sticky) return;
      const tier = this.tiers.find((t) => parseInt(t.dataset.tier, 10) === this.selectedTier);
      if (!tier) return;
      const sub = tier.querySelector('.po-tier__sub');
      this.stickyLabel.textContent = (sub && sub.textContent.trim()) || tier.querySelector('.po-tier__label').textContent;
      this.stickyPrice.textContent = tier.querySelector('[data-price]').textContent;
      const label = this.isAtcAbove() ? this.atcLabel : 'Jetzt auswählen';
      if (this.stickyBtn.textContent !== label) this.stickyBtn.textContent = label;
    }

    isAtcAbove() {
      return this.atc.getBoundingClientRect().bottom < 0;
    }

    // Ausgeblendete Werte (z. B. 3er Set) aus Varianten und Optionen entfernen
    applyHiddenValues() {
      const hidden = String(this.config.hiddenValues || '').split(',').map((v) => v.trim()).filter(Boolean);
      if (!hidden.length) return;
      this.config.variants = this.config.variants.filter((v) => !v.options.some((o) => hidden.includes(o)));
      this.config.options.forEach((option) => {
        option.values = option.values.filter((v) => !hidden.includes(v.value));
      });
    }

    parseLabels(text) {
      const map = {};
      String(text || '').split(/\r?\n/).forEach((line) => {
        const i = line.indexOf('=');
        if (i < 1) return;
        const from = line.slice(0, i).trim();
        const to = line.slice(i + 1).trim();
        if (from && to) map[from] = to;
      });
      return map;
    }

    label(value) {
      return this.labels[value] || value;
    }

    swatch(optionIndex, value) {
      const option = this.config.options[optionIndex];
      const entry = option && option.values.find((v) => v.value === value);
      if (entry && entry.swatch) return entry.swatch;
      if (option && /farbe|color|colour/i.test(option.name)) return FALLBACK_SWATCHES[String(value).toLowerCase()] || null;
      return null;
    }

    variantFor(options) {
      return this.config.variants.find((v) => v.options.every((o, i) => o === options[i]));
    }

    tierItems(n) {
      return this.rows.slice(0, n).map((opts) => this.variantFor(opts));
    }

    giftsForTier(n) {
      return this.gifts.filter((g) => parseInt(g.dataset.tier, 10) <= n);
    }

    renderPerks(savings = {}) {
      this.tiers.forEach((tier) => {
        const n = parseInt(tier.dataset.tier, 10);
        const parts = [];
        if (savings[n] > 0) parts.push(`${formatMoney(savings[n], this.config.moneyFormat)} gespart`);
        // Versand nur in der Stufe nennen, ab der er gilt, Geschenke dagegen alle bis zu dieser Stufe
        const isShipping = (g) => !g.dataset.variant && !g.hasAttribute('data-placeholder') && /versand/i.test(g.dataset.title);
        const gifts = this.giftsForTier(n);
        gifts.filter((g) => isShipping(g) && parseInt(g.dataset.tier, 10) === n).forEach((g) => parts.push(`<strong>${g.dataset.title}</strong>`));
        const presents = gifts.filter((g) => !isShipping(g)).map((g) => g.dataset.title);
        if (presents.length) parts.push(`<strong>${presents.join(' und ')} gratis</strong>`);
        const perks = tier.querySelector('[data-perks]');
        perks.innerHTML = parts.join(' + ');
        perks.hidden = parts.length === 0;
      });
    }

    renderPrices() {
      const format = this.config.moneyFormat;
      const savings = {};
      this.tiers.forEach((tier) => {
        const n = parseInt(tier.dataset.tier, 10);
        const discount = parseFloat(tier.dataset.discount) || 0;
        const items = this.tierItems(n);
        if (items.some((v) => !v)) return;
        // Kaching rundet den Rabatt pro Stück ab (z. B. 59,95 € −10 % = 53,96 €)
        const final = items.reduce((sum, v) => sum + v.price - Math.floor((v.price * discount) / 100), 0);
        const compare = n === 1 ? items.reduce((sum, v) => sum + Math.max(v.compare, v.price), 0) : items.reduce((sum, v) => sum + v.price, 0);
        tier.querySelector('[data-price]').textContent = formatMoney(final, format);
        tier.querySelector('[data-compare]').textContent = compare > final ? formatMoney(compare, format) : '';
        const each = tier.querySelector('[data-each]');
        if (each) each.textContent = `${formatMoney(Math.round(final / n), format)} je Set`;
        savings[n] = items.reduce((sum, v) => sum + v.price, 0) - final;
        if (n === this.selectedTier && this.atcPrice) this.atcPrice.textContent = formatMoney(final, format);
      });
      this.renderPerks(savings);
    }

    renderGifts() {
      let unlocked = 0;
      this.gifts.forEach((g) => {
        const open = parseInt(g.dataset.tier, 10) <= this.selectedTier;
        g.classList.toggle('is-locked', !open);
        if (open) unlocked += 1;
      });
      const count = this.querySelector('[data-gift-count]');
      if (count) count.textContent = `${unlocked} von ${this.gifts.length} freigeschaltet`;
      const value = this.gifts
        .filter((g) => parseInt(g.dataset.tier, 10) <= this.selectedTier)
        .reduce((sum, g) => sum + (parseInt(g.dataset.value, 10) || 0), 0);
      const title = this.querySelector('[data-gift-title]');
      if (title) title.textContent = value > 0 ? `Deine Geschenke im Wert von ${formatMoney(value, this.config.moneyFormat)}` : 'Deine Geschenke';
    }

    renderItems() {
      this.tiers.forEach((tier) => {
        const box = tier.querySelector('[data-items]');
        if (parseInt(tier.dataset.tier, 10) !== this.selectedTier) {
          box.innerHTML = '';
          return;
        }
        box.innerHTML = '';
        for (let i = 0; i < this.selectedTier; i += 1) box.appendChild(this.buildRow(i));
      });
    }

    buildRow(rowIndex) {
      const row = document.createElement('div');
      row.className = 'po-item';
      const variant = this.variantFor(this.rows[rowIndex]);
      const img = document.createElement('img');
      img.className = 'po-item__img';
      img.alt = '';
      img.width = 44;
      img.height = 44;
      img.loading = 'lazy';
      if (variant && variant.image) img.src = variant.image;
      row.appendChild(img);

      let selects = 0;
      const colorIndex = this.config.options.findIndex((o) => /farbe|color|colour/i.test(o.name) && o.values.length > 1);
      const others = this.config.options.filter((o, i) => i !== colorIndex && o.values.length > 1).length;
      if (colorIndex > -1 && others === 0) return this.buildSwatchRow(rowIndex, colorIndex);
      this.config.options.forEach((option, optionIndex) => {
        // Nur ein möglicher Wert: keine Auswahl anzeigen
        if (option.values.length < 2) return;
        selects += 1;
        const wrap = document.createElement('label');
        wrap.className = 'po-select';
        const select = document.createElement('select');
        select.setAttribute('aria-label', `${option.name} für Set ${rowIndex + 1}`);
        option.values.forEach(({ value }) => {
          const opt = document.createElement('option');
          opt.value = value;
          opt.textContent = this.label(value);
          const test = this.rows[rowIndex].slice();
          test[optionIndex] = value;
          const v = this.variantFor(test);
          if (!v || !v.available) opt.textContent += ' (ausverkauft)';
          select.appendChild(opt);
        });
        select.value = this.rows[rowIndex][optionIndex];
        const color = this.swatch(optionIndex, select.value);
        if (color) {
          wrap.classList.add('po-select--swatch');
          const dot = document.createElement('span');
          dot.className = 'po-select__swatch';
          dot.style.background = color;
          wrap.appendChild(dot);
        }
        select.addEventListener('change', () => {
          this.rows[rowIndex][optionIndex] = select.value;
          // Auswahl in Set 1 gilt auch als Vorgabe für die Preise der anderen Stufen
          if (rowIndex === 0) {
            for (let r = this.selectedTier; r < this.rows.length; r += 1) this.rows[r] = this.rows[0].slice();
          }
          this.update();
        });
        wrap.appendChild(select);
        row.appendChild(wrap);
      });
      if (selects === 1) row.classList.add('po-item--single');
      return row;
    }

    // Farbe per Bild antippen statt Auswahlliste
    buildSwatchRow(rowIndex, colorIndex) {
      const row = document.createElement('div');
      row.className = 'po-item po-item--swatches';
      const nice = (v) => String(v).charAt(0).toUpperCase() + String(v).slice(1);
      const title = document.createElement('p');
      title.className = 'po-item__title';
      const current = this.rows[rowIndex][colorIndex];
      title.innerHTML = `Set ${rowIndex + 1}: <b>${nice(this.label(current))}</b>`;
      row.appendChild(title);
      const list = document.createElement('div');
      list.className = 'po-swatches';
      list.setAttribute('role', 'radiogroup');
      list.setAttribute('aria-label', `Farbe für Set ${rowIndex + 1}`);
      this.config.options[colorIndex].values.forEach(({ value }) => {
        const test = this.rows[rowIndex].slice();
        test[colorIndex] = value;
        const v = this.variantFor(test);
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'po-sw' + (value === current ? ' is-active' : '') + (!v || !v.available ? ' is-soldout' : '');
        btn.setAttribute('role', 'radio');
        btn.setAttribute('aria-checked', String(value === current));
        btn.setAttribute('aria-label', nice(this.label(value)) + (!v || !v.available ? ' (ausverkauft)' : ''));
        btn.title = nice(this.label(value));
        if (v && v.image) {
          const img = document.createElement('img');
          img.src = v.image;
          img.alt = '';
          img.width = 44;
          img.height = 44;
          img.decoding = 'async';
          btn.appendChild(img);
        } else {
          btn.style.background = this.swatch(colorIndex, value) || '#ddd';
        }
        btn.addEventListener('click', () => {
          this.rows[rowIndex][colorIndex] = value;
          if (rowIndex === 0) {
            for (let r = this.selectedTier; r < this.rows.length; r += 1) this.rows[r] = this.rows[0].slice();
          }
          this.update();
          const again = this.querySelector(`.po-tier.is-selected .po-item:nth-child(${rowIndex + 1}) .po-sw.is-active`);
          if (again) again.focus({ preventScroll: true });
        });
        list.appendChild(btn);
      });
      row.appendChild(list);
      return row;
    }

    selectTier(n) {
      this.selectedTier = n;
      this.tiers.forEach((tier) => {
        const active = parseInt(tier.dataset.tier, 10) === n;
        tier.classList.toggle('is-selected', active);
        tier.querySelector('.po-tier__radio').checked = active;
      });
      this.update();
    }

    update() {
      this.renderItems();
      this.renderPrices();
      this.renderGifts();
      this.renderSticky();
      const items = this.tierItems(this.selectedTier);
      const ok = items.every((v) => v && v.available);
      const stock = this.querySelector('[data-stock]');
      if (stock) stock.hidden = !ok;
      this.atc.disabled = !ok;
      this.showError(ok ? '' : 'Diese Kombination ist gerade nicht verfügbar.');
    }

    showError(message) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = !message;
    }

    async addToCart() {
      const items = this.tierItems(this.selectedTier);
      if (items.some((v) => !v || !v.available)) return;
      let merged = [];
      items.forEach((v) => {
        const line = merged.find((l) => l.id === v.id);
        if (line) line.quantity += 1;
        else merged.push({ id: v.id, quantity: 1 });
      });

      this.atc.setAttribute('aria-busy', 'true');
      this.showError('');
      try {
        const cart = await fetch('/cart.js').then((r) => r.json()).catch(() => ({ items: [] }));
        // Mit Kaching-Deal vergibt Kaching Mengenrabatt und Geschenk zusammen (ein Rabatt, kein Konflikt)
        const deal = window.poKaching ? window.poKaching.dealFrom(document, this.config.productId) : null;
        if (deal) {
          merged = await window.poKaching.lines(deal, cart, this.config.productId, items.map((v) => v.id), this.config.addGifts);
        } else if (this.config.addGifts) {
          const inCart = new Set(cart.items.filter((i) => i.properties && i.properties._po_gift).map((i) => i.variant_id));
          this.giftsForTier(this.selectedTier).forEach((g) => {
            const id = parseInt(g.dataset.variant, 10);
            if (!id || inCart.has(id)) return;
            merged.push({ id, quantity: 1, properties: { Geschenk: 'gratis', _po_gift: String(g.dataset.tier), _po_for: String(this.config.productId) } });
          });
        }

        const drawer = document.querySelector('cart-drawer');
        const body = { items: merged };
        if (drawer && typeof drawer.getSectionsToRender === 'function') {
          body.sections = drawer.getSectionsToRender().map((s) => s.id);
          body.sections_url = window.location.pathname;
        }
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        });
        const json = await res.json();
        if (!res.ok || json.status) {
          this.showError(json.description || json.message || 'Das hat leider nicht geklappt. Bitte versuche es noch einmal.');
          return;
        }
        if (drawer && typeof drawer.renderContents === 'function' && json.sections) {
          drawer.classList.remove('is-empty');
          drawer.renderContents(json);
        } else {
          window.location.href = (window.Shopify && Shopify.routes ? Shopify.routes.root : '/') + 'cart';
        }
      } catch (e) {
        this.showError('Das hat leider nicht geklappt. Bitte versuche es noch einmal.');
      } finally {
        this.atc.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('po-bundle', PoBundle);
})();
